import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Form,
  Input,
  Button,
  Space,
  Select,
  Image,
  Tag,
  Typography,
  message,
} from 'antd';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTable } from '@/hooks/useTable';
import { getSubmissionArtworkList, getAwardTypes, getNewcomerChallengeDetail, type AwardType } from './api';
import type { SubmissionArtworkListParams, SubmissionArtwork, NewcomerChallenge } from './types';
import { ARTWORK_COVER_PLACEHOLDER } from '@/consts/image';

const { Text } = Typography;

const STATUS_MAP: Record<number, { text: string; color: string }> = {
  0: { text: '未开始', color: 'default' },
  1: { text: '投稿中', color: 'processing' },
  2: { text: '评比中', color: 'warning' },
  3: { text: '已结束', color: 'success' },
};

const NewcomerChallengeSubmissionsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const challengeId = Number(id) || undefined;
  const status = (location.state as any)?.status;

  const [awardTypes, setAwardTypes] = useState<AwardType[]>([]);
  const [loadingAwardTypes, setLoadingAwardTypes] = useState(false);
  const [challengeDetail, setChallengeDetail] = useState<NewcomerChallenge | null>(null);

  const [searchParams, setSearchParams] = useState<SubmissionArtworkListParams>({
    rookieKingChallengeId: challengeId,
    currentPage: 1,
    pageSize: 10,
  });

  // 加载奖励类型列表
  useEffect(() => {
    setLoadingAwardTypes(true);
    getAwardTypes()
      .then((res) => {
        if ((res.status === 200 || res.success) && res.data && Array.isArray(res.data)) {
          setAwardTypes(res.data);
        }
      })
      .catch(() => {
        message.error('获取奖励类型列表失败');
      })
      .finally(() => {
        setLoadingAwardTypes(false);
      });
  }, []);

  // 加载挑战赛详情（获取获奖人数信息）
  useEffect(() => {
    if (challengeId) {
      getNewcomerChallengeDetail(challengeId)
        .then((res) => {
          if (res.status === 200 && res.data) {
            setChallengeDetail(res.data);
          }
        })
        .catch(() => {
          // 静默失败，不影响列表显示
        });
    }
  }, [challengeId]);

  // 根据 dayType 分类奖励类型
  const awardTypes3Days = awardTypes.filter((item) => item.dayType === 3);
  const awardTypes7Days = awardTypes.filter((item) => item.dayType === 7);

  const {
    data,
    total,
    currentPage,
    pageSize,
    isLoading,
    handleTableChange,
    updateParams,
  } = useTable({
    queryKey: 'newcomer-challenge-submissions',
    queryFn: async (current, size, params) => {
      const response = await getSubmissionArtworkList({
        ...params,
        rookieKingChallengeId: challengeId,
        currentPage: current,
        pageSize: size,
      });
      return response.data;
    },
    defaultPageSize: 10,
    defaultParams: searchParams,
  });

  const handleSearch = async () => {
    const values = await form.validateFields();
    setSearchParams({ ...searchParams, ...values });
    updateParams({ ...searchParams, ...values });
  };

  const handleReset = () => {
    form.resetFields();
    setSearchParams({
      rookieKingChallengeId: challengeId,
      currentPage: 1,
      pageSize: 10,
    });
    updateParams({
      rookieKingChallengeId: challengeId,
      currentPage: 1,
      pageSize: 10,
    });
  };

  const columns = [
    {
      title: '作品图片',
      dataIndex: 'artworkImageUrl',
      key: 'artworkImageUrl',
      width: 120,
      render: (url: string) => (
        <Image
          src={url || ARTWORK_COVER_PLACEHOLDER}
          fallback={ARTWORK_COVER_PLACEHOLDER}
          alt="作品图片"
          style={{ width: 80, height: 80, objectFit: 'cover' }}
        />
      ),
    },
    {
      title: '作品标题',
      dataIndex: 'artworkTitle',
      key: 'artworkTitle',
      width: 200,
    },
    {
      title: '用户信息',
      key: 'userInfo',
      width: 150,
      render: (_: any, record: SubmissionArtwork) => (
        <div>
          <div>Name: {record.userName}</div>
          <div style={{ color: '#999' }}>ID: {record.userSuppId}</div>
        </div>
      ),
    },
    {
      title: '用户身份',
      dataIndex: 'vipName',
      key: 'vipName',
      width: 120,
      render: (vipName: string) => {
        if (!vipName) return '-';
        return vipName.replace(/Plus/g, 'Pro');
      },
    },
    {
      title: '数据统计',
      key: 'statistics',
      width: 300,
      render: (_: any, record: SubmissionArtwork) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          <span>热度: {record.hotScore || 0}</span>
          <span>票数: {record.voteNum || 0}</span>
          <span>点赞: {record.likeNum || 0}</span>
          <span>收藏: {record.collectNum || 0}</span>
          <span>评论: {record.commentNum || 0}</span>
        </div>
      ),
    }, 
    {
      title: '3日获奖情况',
      key: 'award3Days',
      width: 250,
      render: (_: any, record: SubmissionArtwork) => (
        <div>
          <div>排名: {record.threeDayRanking || '-'}</div>
          <div>获奖状态: {record.threeDayRewardType || '未获奖'}</div>
        </div>
      ),
    },
    {
      title: '7日获奖情况',
      key: 'award7Days',
      width: 250,
      render: (_: any, record: SubmissionArtwork) => (
        <div>
          <div>排名: {record.sevenDayRanking || '-'}</div>
          <div>获奖状态: {record.sevenDayRewardType || '未获奖'}</div>
        </div>
      ),
    },
  ];

  return (
    <Card
      title={
        <div>
          <Button
            type="link"
            onClick={() => navigate(-1)}
            style={{ padding: 0, marginBottom: 8 }}
          >
            &lt; 返回
          </Button>
          <div>新人王投稿作品</div>
        </div>
      }
      extra={
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {status !== undefined && (
            <Text type="secondary">
              {STATUS_MAP[status]?.text || '未知状态'}
            </Text>
          )}
          <Text type="secondary">
            当前获奖人数 {challengeDetail?.rewardWinnerCount ?? 0}/{challengeDetail?.rewardConfigWinnerCount ?? 0}
          </Text>
        </div>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline">
          <Form.Item name="artworkTitle">
            <Input placeholder="请输入作品标题" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="userName">
            <Input placeholder="请输入用户名" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="userSuppId">
            <Input placeholder="请输入用户ID" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="vipName">
            <Input placeholder="请输入用户身份vipName" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="threeDayRewardType">
            <Select
              style={{ width: 180 }}
              placeholder="请选择3日获奖状态"
              allowClear
              loading={loadingAwardTypes}
              options={awardTypes3Days.map((item) => ({
                label: item.typeName || `类型${item.id}`,
                value: item.id,
              }))}
            />
          </Form.Item>
          <Form.Item name="sevenDayRewardType">
            <Select
              style={{ width: 180 }}
              placeholder="请选择7日获奖状态"
              allowClear
              loading={loadingAwardTypes}
              options={awardTypes7Days.map((item) => ({
                label: item.typeName || `类型${item.id}`,
                value: item.id,
              }))}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" onClick={handleSearch}>
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="artworkId"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
        }}
        loading={isLoading}
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
      />
    </Card>
  );
};

export default NewcomerChallengeSubmissionsPage;

